import { useMutation, useQueryClient } from '@tanstack/react-query';
import { subscriptionPlanService } from '@services/subscription-plan';
import { toast } from 'react-toastify';
import { useTranslation } from '@hooks/useTranslation';

/**
 * Sprint 17, story-082 — SUPER_ADMIN only: link/unlink/reprice an add-on on a base plan, from
 * `CreateEditPlanModal`'s "Linked Add-ons" section. Each invalidates `subscriptionPlans` (this
 * admin list's own query key) — the server-side cache invalidation (`CacheTag.SubscriptionPlan`)
 * is what keeps `getSubscriptionPlans`/`getLinkedAddOnsForOrganization` correct for every other
 * consumer.
 */
export const useLinkAddonToPlan = () => {
  const tActionMessages = useTranslation('actionMessages');
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ planId, addonId, priceCents }: { planId: string; addonId: string; priceCents: number }) =>
      subscriptionPlanService.linkAddonToPlan(planId, addonId, priceCents),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subscriptionPlans'] });
    },
    onError: (error: any) => {
      toast.error(error?.response?.errors?.[0]?.message || tActionMessages.failedToLinkAddon);
    },
  });
};

export const useUnlinkAddonFromPlan = () => {
  const tActionMessages = useTranslation('actionMessages');
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ planId, addonId }: { planId: string; addonId: string }) =>
      subscriptionPlanService.unlinkAddonFromPlan(planId, addonId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subscriptionPlans'] });
    },
    onError: (error: any) => {
      toast.error(error?.response?.errors?.[0]?.message || tActionMessages.failedToUnlinkAddon);
    },
  });
};

export const useRepriceAddonLink = () => {
  const tActionMessages = useTranslation('actionMessages');
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ planId, addonId, priceCents }: { planId: string; addonId: string; priceCents: number }) =>
      subscriptionPlanService.repriceAddonLink(planId, addonId, priceCents),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subscriptionPlans'] });
    },
    onError: (error: any) => {
      toast.error(error?.response?.errors?.[0]?.message || tActionMessages.failedToRepriceAddon);
    },
  });
};
